import { Link } from 'react-router'
import { Button, Card, SectionLabel } from '../../../design/components'
import type { CampaignCoreSection } from '../types'
import styles from './sections.module.css'

/**
 * The way into the campaign's cases and encounters.
 *
 * They hang off a campaign exactly as the city does, and they are their own reads, so the
 * campaign page links to them rather than loading them. The counts here are the two the
 * campaign sheet already carries; a case or encounter count would be two more queries for
 * a number nobody acts on.
 *
 * **Encounters appear only for the Game Master.** They are GM-only for reads as well as
 * writes — a participant's card is an entire NPC sheet — so a player is not shown a door
 * that would answer 403. That is the implementation plan's "the UI must not show an
 * encounters entry point to players", taken literally.
 */
export function PlayCard({
  campaignId,
  core,
  isGameMaster,
}: {
  campaignId: number
  core: CampaignCoreSection | undefined
  isGameMaster: boolean
}) {
  if (core === undefined) return null

  return (
    <Card variant="powers" label="In play">
      <div className={styles.namedLine}>
        <SectionLabel tone="muted" micro>
          Cases
        </SectionLabel>
        <span className={styles.rowText}>
          A case is one story: the characters it turns on, and the scenes it runs through.
        </span>
      </div>

      <div className={styles.actions}>
        <Link to={`/campaigns/${campaignId}/cases`} className={styles.cardLink}>
          <Button variant="secondary" fullWidth>
            Cases
          </Button>
        </Link>

        {/* Comics are a member read — any player may look at what has been drawn — so
            unlike encounters this door is open to everyone. Only the Game Master finds
            anything to press once through it. */}
        <Link to={`/campaigns/${campaignId}/comics`} className={styles.cardLink}>
          <Button variant="secondary" fullWidth>
            Comics
          </Button>
        </Link>

        {isGameMaster && (
          <Link to={`/campaigns/${campaignId}/encounters`} className={styles.cardLink}>
            <Button variant="secondary" fullWidth>
              Encounters
            </Button>
          </Link>
        )}
      </div>
    </Card>
  )
}
